/** 도면 불러오기 목록 · 내 정보(이름·이메일·연락처 + 비밀번호 변경) 창 */
import React, { useEffect, useState } from 'react';
import { DrawingListItem, listDrawings, deleteDrawing, changePassword, fetchProfile, saveProfile, errMsg } from '../utils/api';
import { useAuth } from './AuthGate';
import { overlay, box, head, xBtn, inp, btn, smBtn, label } from './ui';

// 서버 시각(DB 현지 시각) 그대로 표시: 2026-10-24T19:43:07.500Z → 2026-10-24 19:43
const fmt = (s: string) => (s || '').replace('T', ' ').slice(0, 16);

export const LoadDialog: React.FC<{ current?: string | null; onLoad: (no: string) => void; onClose: () => void }> = ({ current, onLoad, onClose }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<DrawingListItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = (query: string) => listDrawings(query).then(r => { setRows(r.data); setErr(null); }).catch(e => setErr(errMsg(e)));
  useEffect(() => { load(''); }, []);
  const remove = async (r: DrawingListItem) => {
    if (!window.confirm(`${r.ESTI_NO} ${r.SITE_NM || ''} 도면을 삭제할까요? 되돌릴 수 없습니다.`)) return;
    try { await deleteDrawing(r.ESTI_NO); load(q); } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <div style={overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...box, maxWidth: 760 }}>
        <div style={head()}><b>도면 불러오기</b><button onClick={onClose} style={xBtn}>✕</button></div>
        <div style={{ padding: '4px 24px 12px', display: 'flex', gap: 8 }}>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(q)} placeholder="현장명 또는 견적번호"
            style={{ ...inp, flex: 1 }} />
          <button onClick={() => load(q)} style={btn()}>검색</button>
        </div>
        {err && <div style={{ color: '#F04452', padding: '0 24px 8px', fontSize: 13 }}>⚠ {err}</div>}
        <div style={{ overflow: 'auto', padding: '0 24px 20px' }}>
          {rows === null ? <div style={{ color: '#888', padding: 12 }}>불러오는 중…</div>
            : rows.length === 0 ? <div style={{ color: '#888', padding: 12 }}>저장된 도면이 없습니다</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr>
                  {['견적번호', '현장명', '규격', '두께', '수정', ''].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 8px', borderBottom: '1px solid #E5E8EB', color: '#8B95A1', fontWeight: 600, fontSize: 12 }}>{h}</th>)}
                </tr></thead>
                <tbody>{rows.map(r => (
                  <tr key={r.ESTI_NO} onClick={() => onLoad(r.ESTI_NO)} style={{ cursor: 'pointer', background: r.ESTI_NO === current ? '#E8F3FF' : undefined }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#F4F9FF')} onMouseLeave={e => (e.currentTarget.style.background = r.ESTI_NO === current ? '#E8F3FF' : '')}>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #F2F4F6', fontVariantNumeric: 'tabular-nums' }}>{r.ESTI_NO}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #F2F4F6' }}>{r.SITE_NM || <span style={{ color: '#aaa' }}>(현장명 없음)</span>}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #F2F4F6' }}>{r.HORIZONTAL}×{r.VERTICAL}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #F2F4F6' }}>{r.PANEL_THICK}T</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #F2F4F6', color: '#666' }}>{fmt(r.UPT_DT)} · {r.UPT_ID}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #F2F4F6', textAlign: 'right' }}>
                      <button onClick={e => { e.stopPropagation(); remove(r); }} style={smBtn('#F04452', false)}>삭제</button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
};

export const PasswordDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [next2, setNext2] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const bad = next.length < 8 ? '새 비밀번호는 8자 이상' : next !== next2 ? '새 비밀번호가 서로 다름' : null;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (bad) return;
    try { await changePassword(cur, next); setMsg({ ok: true, text: '변경했습니다' }); setCur(''); setNext(''); setNext2(''); }
    catch (ex) { setMsg({ ok: false, text: errMsg(ex) }); }
  };
  const input: React.CSSProperties = { ...inp, width: '100%', margin: '0 0 12px' };
  return (
    <div style={overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...box, maxWidth: 380, overflowY: 'auto' }}>
        <div style={head()}><b>내 정보</b><button type="button" onClick={onClose} style={xBtn}>✕</button></div>
        <ProfileForm />
      <form onSubmit={submit}>
        <div style={{ padding: '4px 24px 22px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, margin: '4px 0 10px' }}>비밀번호 변경</div>
          <label style={label}>현재 비밀번호</label>
          <input type="password" value={cur} onChange={e => setCur(e.target.value)} autoComplete="current-password" style={input} autoFocus />
          <label style={label}>새 비밀번호 (8자 이상)</label>
          <input type="password" value={next} onChange={e => setNext(e.target.value)} autoComplete="new-password" style={input} />
          <label style={label}>새 비밀번호 확인</label>
          <input type="password" value={next2} onChange={e => setNext2(e.target.value)} autoComplete="new-password" style={input} />
          {(msg || (next && bad)) && <div style={{ fontSize: 13, marginBottom: 10, color: msg?.ok ? '#03B26C' : '#F04452' }}>{msg ? msg.text : `⚠ ${bad}`}</div>}
          <button type="submit" disabled={!!bad || !cur} style={{ ...btn(), width: '100%', height: 48, opacity: bad || !cur ? 0.4 : 1 }}>비밀번호 변경</button>
        </div>
      </form>
      </div>
    </div>
  );
};

/** 이름·이메일(비밀번호 찾기용)·연락처 — 이전 시스템 userFormPerson.jsp */
const ProfileForm: React.FC = () => {
  const { user, update } = useAuth();
  const [f, setF] = useState<{ name: string; email: string; tel: string } | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    fetchProfile().then(r => setF({ name: r.data.USER_NM || '', email: r.data.EMAIL || '', tel: r.data.TEL_NO || '' })).catch(e => setMsg({ ok: false, text: errMsg(e) }));
  }, []);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f) return;
    try { const r = await saveProfile(f); update?.(r.data); setMsg({ ok: true, text: '저장했습니다' }); } catch (ex) { setMsg({ ok: false, text: errMsg(ex) }); }
  };
  const input: React.CSSProperties = { ...inp, width: '100%', margin: '0 0 12px' };
  return (
    <form onSubmit={submit} style={{ padding: '4px 24px 18px', borderBottom: '8px solid #F2F4F6' }}>
      <div style={{ fontSize: 13, color: '#8B95A1', marginBottom: 10 }}>아이디 <b style={{ color: '#333D4B' }}>{user.id}</b> · {user.compName}</div>
      {!f ? <div style={{ color: msg ? '#F04452' : '#888', fontSize: 13 }}>{msg?.text ?? '불러오는 중…'}</div> : <>
        <label style={label}>이름</label>
        <input value={f.name} onChange={e => { setF({ ...f, name: e.target.value }); setMsg(null); }} maxLength={20} style={input} />
        <label style={label}>이메일 (아이디·비밀번호 찾기에 씀)</label>
        <input type="email" value={f.email} onChange={e => { setF({ ...f, email: e.target.value }); setMsg(null); }} maxLength={50} style={input} />
        <label style={label}>연락처 (견적서 담당자 연락처)</label>
        <input value={f.tel} onChange={e => { setF({ ...f, tel: e.target.value }); setMsg(null); }} maxLength={20} placeholder="010-0000-0000" style={input} />
        {msg && <div style={{ fontSize: 13, marginBottom: 10, color: msg.ok ? '#03B26C' : '#F04452' }}>{msg.ok ? '✓' : '⚠'} {msg.text}</div>}
        <button type="submit" disabled={!f.name.trim()} style={{ ...btn(), width: '100%', height: 44 }}>저장</button>
      </>}
    </form>
  );
};
